/**
 * Block 12 PR-B Phase B3 Part C Step 14 — computeTotal.
 *
 * Live total всех секций сметы AnketaForm. Используется `<SectionTotal>`
 * (Step 23) для отображения + `useAnketaPersist` (Step 12) при IDB записи
 * (replaces placeholder total=0 from Step 12).
 *
 * Сумма (методика §1.5 = qty × price = cost):
 *   - Section Б Materials: sum(materialRows[i].cost) — derived via computeMaterialRows.
 *   - Section В Additional: sum по enabled (toggle=true) ids, qty * price.
 *   - Section Г Filling (только Cabinet/Balcony): sum по enabled.
 *   - Section Д Lighting (master toggle): sum 4 fixed lighting rows.
 *   - Section Е Consumables (5 toggleable): sum по enabled.
 *   - Section Ж Services (6 rows: 3 auto-bind + 3 manual): sum через buildServiceRow.
 *   - Section З Custom (free-form): sum via selectCustomRowsCost.
 *
 * P-006 (Edge как authoritative): client computes для preview UI, server
 * pересчитывает при save → `CalculatorProjectPayload.total`.
 *
 * Reference: ADR-0020 §Q4 (override pattern), §Q5 (auto-bind services), методика §1.4.
 */

import type { CalculatorMethodology } from '@pomoshnik/shared';
import { selectCustomRowsCost } from '../state/selectors';
import type { AnketaState } from '../types';
import { buildServiceRow, SERVICE_ROW_SPECS } from './computeAutoServiceQty';
import { computeMaterialRows } from './computeMaterialRows';
import { computeVitrineCost } from './vitrineFormulas';

const VITRINE_ROW_ID = 'aluminum-glass-vitrine';

/**
 * Summa enabled rows of a toggleable section (Additional/Filling/Consumable).
 * Игнорирует строки без toggle=true (даже если qty/price заполнены — user
 * мог toggle off + строка стала «черновой»).
 */
function sumToggleableSection(
  toggles: Record<string, boolean>,
  quantities: Record<string, number>,
  prices: Record<string, number>,
): number {
  let total = 0;
  for (const [id, on] of Object.entries(toggles)) {
    if (!on) continue;
    const qty = quantities[id] ?? 0;
    const price = prices[id] ?? 0;
    total += qty * price;
  }
  return total;
}

/**
 * Phase B4 Step 9 — Section В Additional total с per-id override для Витрина
 * (`id === 'aluminum-glass-vitrine'`). Витрина использует compound formula
 * `(length × height) / 1_000_000 × pricePerM2` (см. `vitrineFormulas.ts`)
 * вместо qty × price. Остальные additional ids используют generic qty × price.
 *
 * Игнорирует строки без toggle=true (same pattern как sumToggleableSection).
 */
function sumAdditionalSection(state: AnketaState): number {
  let total = 0;
  for (const [id, on] of Object.entries(state.additionalToggles)) {
    if (!on) continue;
    if (id === VITRINE_ROW_ID) {
      total += computeVitrineCost(
        state.additionalLengths[id] ?? 0,
        state.additionalHeights[id] ?? 0,
        state.additionalPricesPerM2[id] ?? 0,
      );
      continue;
    }
    const qty = state.additionalQuantities[id] ?? 0;
    const price = state.additionalPrices[id] ?? 0;
    total += qty * price;
  }
  return total;
}

/**
 * Lighting section sum (Section Д). Master toggle `state.lightingEnabled`:
 * выкл → 0; вкл → sum по union ключей quantities + prices.
 *
 * Берём union, чтобы row с только-price (без qty seeded) не пропускался: для
 * lighting rows qty=0 даёт cost=0 (правильно), но iterating только quantities
 * пропустил бы такую строку из итерации — discrepancy hard to debug. Phase B3
 * Step 20 со-seed'ит все 4 ids, но defensive union на случай edge cases.
 */
function sumLightingSection(state: AnketaState): number {
  if (!state.lightingEnabled) return 0;
  const ids = new Set([
    ...Object.keys(state.lightingQuantities),
    ...Object.keys(state.lightingPrices),
  ]);
  let total = 0;
  for (const id of ids) {
    const qty = state.lightingQuantities[id] ?? 0;
    const price = state.lightingPrices[id] ?? 0;
    total += qty * price;
  }
  return total;
}

function sumServiceSection(
  state: AnketaState,
  materialRows: ReturnType<typeof computeMaterialRows>,
): number {
  let total = 0;
  for (const spec of SERVICE_ROW_SPECS) {
    if (!state.serviceToggles[spec.id]) continue;
    const row = buildServiceRow({
      spec,
      materialRows,
      serviceOverrides: state.serviceOverrides,
      servicePrices: state.servicePrices,
    });
    total += row.cost;
  }
  return total;
}

/**
 * Полный расчёт total сметы. Pure — same inputs → same output. Используется
 * `<SectionTotal>` для live display + `useAnketaPersist` для записи в payload.
 */
export function computeTotal(state: AnketaState, methodology: CalculatorMethodology): number {
  const materialRows = computeMaterialRows(state, methodology);
  const materialTotal = materialRows.reduce((sum, r) => sum + r.cost, 0);
  const additionalTotal = sumAdditionalSection(state);
  const fillingTotal = sumToggleableSection(
    state.fillingToggles,
    state.fillingQuantities,
    state.fillingPrices,
  );
  const lightingTotal = sumLightingSection(state);
  const consumableTotal = sumToggleableSection(
    state.consumableToggles,
    state.consumableQuantities,
    state.consumablePrices,
  );
  const serviceTotal = sumServiceSection(state, materialRows);
  const customTotal = selectCustomRowsCost(state);
  return (
    materialTotal +
    additionalTotal +
    fillingTotal +
    lightingTotal +
    consumableTotal +
    serviceTotal +
    customTotal
  );
}
